import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PushTab() {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const handleSend = async () => {
    if (!message.trim()) {
      setStatus('error');
      setTimeout(() => setStatus(null), 3000);
      return;
    }

    try {
      setSending(true);
      setStatus(null);

      await axios.post(
        `${API}/admin/push/send-instant`,
        { message: message.trim() },
        { withCredentials: true }
      );

      setStatus('success');
      setMessage(''); // Clear message after successful send
      
      // Reset success status after 3 seconds
      setTimeout(() => setStatus(null), 3000);
    } catch (error) {
      console.error('Error sending push notification:', error);
      setStatus('error');
      setTimeout(() => setStatus(null), 3000);
    } finally {
      setSending(false);
    }
  };

  const charCount = message.length;
  const maxChars = 500;
  const isOverLimit = charCount > maxChars;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="push-tab">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-light tracking-wide text-white">
          Instant Push Notification
        </h2>
        <p className="text-gray-400 text-sm font-light">
          Στείλε άμεση ειδοποίηση σε όλους τους χρήστες (π.χ. ακύρωση ραντεβού, κενό slot)
        </p>
      </div>

      {/* Message Input */}
      <div className="space-y-3">
        <div className="relative">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Γράψε το μήνυμα σου εδώ... (π.χ. 'Κενό ραντεβού αύριο στις 14:00!')"
            rows={6}
            className="w-full bg-white/5 border border-white/15 text-white placeholder:text-gray-500 focus:border-white/30 resize-none"
            disabled={sending}
          />
          
          {/* Character Counter */}
          <div className={`absolute bottom-3 right-3 text-xs font-light ${
            isOverLimit ? 'text-red-400' : 'text-gray-500'
          }`}>
            {charCount} / {maxChars}
          </div>
        </div>

        {/* Warning for over limit */}
        {isOverLimit && (
          <div className="flex items-center gap-2 text-red-400 text-sm font-light">
            <AlertCircle className="w-4 h-4" />
            Το μήνυμα είναι πολύ μεγάλο (μέγιστο {maxChars} χαρακτήρες)
          </div>
        )}
      </div>

      {/* Send Button */}
      <div className="flex items-center gap-4">
        <Button
          onClick={handleSend}
          disabled={sending || !message.trim() || isOverLimit}
          className="bg-white text-black hover:bg-gray-200 font-light tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
              Αποστολή...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Αποστολή σε Όλους
            </>
          )}
        </Button>

        {/* Status Messages */}
        {status === 'success' && (
          <div className="flex items-center gap-2 text-green-400 text-sm font-light">
            <Check className="w-4 h-4" />
            Η ειδοποίηση στάλθηκε επιτυχώς!
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-400 text-sm font-light">
            <AlertCircle className="w-4 h-4" />
            Σφάλμα αποστολής. Προσπάθησε ξανά.
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="border border-white/10 bg-white/5 p-4 space-y-2">
        <p className="text-gray-400 text-xs font-light tracking-wide uppercase">
          Πληροφορίες
        </p>
        <ul className="text-gray-300 text-sm font-light space-y-1 list-disc list-inside">
          <li>Η ειδοποίηση θα σταλεί σε όλους τους εγγεγραμμένους χρήστες</li>
          <li>Δεν αποθηκεύεται στις Ανακοινώσεις (μόνο push notification)</li>
          <li>Ιδανικό για έκτακτες ανακοινώσεις και ακυρώσεις ραντεβού</li>
          <li>Οι χρήστες θα λάβουν instant notification στο device τους</li>
        </ul>
      </div>

      {/* Examples */}
      <div className="border border-white/10 bg-white/5 p-4 space-y-2">
        <p className="text-gray-400 text-xs font-light tracking-wide uppercase">
          Παραδείγματα Μηνυμάτων
        </p>
        <div className="space-y-2">
          <button
            onClick={() => setMessage('⚡ Κενό ραντεβού αύριο στις 14:00! Κλείσε τώρα!')}
            className="w-full text-left text-gray-300 text-sm font-light hover:text-white transition-colors border border-white/10 p-2 hover:bg-white/5"
            disabled={sending}
          >
            "⚡ Κενό ραντεβού αύριο στις 14:00! Κλείσε τώρα!"
          </button>
          <button
            onClick={() => setMessage('❌ Ακύρωση ραντεβού σήμερα 18:00. Νέο διαθέσιμο slot!')}
            className="w-full text-left text-gray-300 text-sm font-light hover:text-white transition-colors border border-white/10 p-2 hover:bg-white/5"
            disabled={sending}
          >
            "❌ Ακύρωση ραντεβού σήμερα 18:00. Νέο διαθέσιμο slot!"
          </button>
          <button
            onClick={() => setMessage('🎉 Ειδική προσφορά σήμερα: -20% σε όλα τα προϊόντα!')}
            className="w-full text-left text-gray-300 text-sm font-light hover:text-white transition-colors border border-white/10 p-2 hover:bg-white/5"
            disabled={sending}
          >
            "🎉 Ειδική προσφορά σήμερα: -20% σε όλα τα προϊόντα!"
          </button>
        </div>
      </div>
    </div>
  );
}

export default PushTab;
